import moment from "moment";
import React, { useState } from "react";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import MuiDialogContent from "@material-ui/core/DialogContent";
import MuiDialogActions from "@material-ui/core/DialogActions";
import IconButton from "@material-ui/core/IconButton";

import { useIntl, FormattedMessage, defineMessages } from "react-intl";
import { useLocalizedName } from "../../globals/langHelpers";
import CloseMenuIcon from "../common/icons/closeMenuIcon";
import Button from "../common/button/button";
import DateTimePicker from "../common/datepicker/dateTimePicker";
import Dropdown from "../common/dropdown/dropdown";

const messages = defineMessages({
    requestNewDueDate: {
        id: "rdd-dialog.title",
        defaultMessage: "Request new due date",
        description: "Request new due date dialog title",
    },
    currentDueDate: {
        id: "rdd-dialog.current-due-date",
        defaultMessage: "Current due date: ",
        description: "Current due date dialog title",
    },
    extensionReason: {
        id: "rdd-dialog.extension-reason",
        defaultMessage: "Reason for extension",
        description: "Reason for extension placeholder text",
    },
    extensionSubReason: {
        id: "rdd-dialog.extension-sub-reason",
        defaultMessage: "Sub-reason for extension",
        description: "Sub-reason for extension placeholder text",
    },
    newDueDate: {
        id: "rdd-dialog.new-due-date",
        defaultMessage: "New due date",
        description: "New due date placeholder text",
    },
    requestComments: {
        id: "rdd-dialog.request-comment-field",
        defaultMessage: "Add your comments here",
        description: "Request new due date comments",
    },
});

const styles = (theme) => ({
    title: {
        margin: 0,
        borderTopRightRadius: "10px",
        borderTopLeftRadius: "10px",
        backgroundColor: "var(--grey-keyline)",
        height: 54,
        padding: "16px 32px",

        fontFamily: "BellSlimBlack",
        fontStyle: "normal",
        fontWeight: "900",
        fontSize: "18px",
        lineHeight: "22px",
        letterSpacing: "0.0018em",

        color: "var(--text-dark)",
    },
    closeButton: {
        position: "absolute",
        right: "22px",
        top: "4px",
        color: "var(--blue-bell)",
    },
});

const DialogTitle = withStyles(styles)((props) => {
    const { children, classes, onClose, ...other } = props;
    return (
        <MuiDialogTitle disableTypography className={classes.title} {...other}>
            <div>{children}</div>
            {onClose ? (
                <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
                    <CloseMenuIcon title="Close Dialog" />
                </IconButton>
            ) : null}
        </MuiDialogTitle>
    );
});

const DialogContent = withStyles((theme) => ({
    root: {
        padding: "16px 32px",
    },
}))(MuiDialogContent);

const DialogActions = withStyles((theme) => ({
    root: {
        margin: 0,
        padding: "8px 32px 16px 32px",
    },
}))(MuiDialogActions);

const useStyles = makeStyles((theme) => ({
    paper: {
        borderRadius: "10px",
    },
    characterCount: {
        float: "right",
        marginTop: "-20px",
        marginRight: "15px",
        fontSize: "10px",
        color: "#999999",
    },
    addComment: {
        marginTop: "20px",
    },
    currentDueDate: {
        color: "var(--text-medium)",
        fontFamily: "Arial",
        fontSize: "16px",
    },
}));

const RequestDueDateModal = (props) => {
    const [disableBackdropClick, setDisableBackdropClick] = useState(false);
    const [newDueDate, setNewDueDate] = useState(null);
    const [reason, setReason] = useState(null);
    const [subReason, setSubReason] = useState(null);
    const [comments, setComments] = useState("");
    const [dateError, setDateError] = useState(null);
    const [reasonSubReasons, setReasonSubReasons] = useState([]);

    const { getLocalizedName } = useLocalizedName();
    const classes = useStyles();
    const intl = useIntl();

    const enabled = newDueDate && reason && subReason && comments && dateError === null;

    const doDueDateRequest = () => {
        var request = {
            workOrderNumber: props.workOrder,
            reasonCode: reason.id,
            subReasonCode: subReason.id,
            comments: comments,
            newDueDate: moment.utc(newDueDate).format("YYYY-MM-DD HH:mm:00"),
        };
        props.onAccept(request);
    };

    const onChangeReason = (val) => {
        //let val = e.target.value;
        let sReasons = props.subreasons.filter((obj) => obj.parentReasonCode === val.id);
        setReason(val);
        setReasonSubReasons(sReasons);
    };

    return (
        <div>
            <Dialog
                className="cmis-dialog"
                onClose={props.onCancel}
                aria-labelledby="customized-dialog-title"
                open={props.show}
                PaperProps={{
                    className: classes.paper,
                }}
                style={{
                    borderRadius: "10px",
                }}
                disableBackdropClick={disableBackdropClick}>
                <DialogTitle id="customized-dialog-title" onClose={props.onCancel}>
                    {intl.formatMessage(messages.requestNewDueDate)}
                </DialogTitle>
                <DialogContent>
                    <div className={classes.currentDueDate}>
                        {intl.formatMessage(messages.currentDueDate)}
                        {props?.workOrder?.appointmentDueDate ? (
                            moment.utc(props?.workOrder?.appointmentDueDate).add(moment().utcOffset(), "minutes").format("MMM DD, YYYY")
                        ) : (
                            <FormattedMessage id="rdd-dialog.no-due-date" defaultMessage="No due date" description="No due date text" />
                        )}
                    </div>
                    <Dropdown
                        id="extension-reason"
                        label={intl.formatMessage(messages.extensionReason)}
                        display={(item) => getLocalizedName({ object: item })}
                        itemValue={(item) => item.id} //ID
                        items={props.reasons}
                        value={reason}
                        onChange={(e) => onChangeReason(e)}
                    />
                    <Dropdown
                        id="extension-sub-reason"
                        label={intl.formatMessage(messages.extensionSubReason)}
                        display={(item) => getLocalizedName({ object: item })}
                        itemValue={(item) => item.id} //ID
                        items={reasonSubReasons}
                        value={subReason}
                        onChange={setSubReason}
                    />

                    <DateTimePicker
                        minDate="today"
                        label={intl.formatMessage(messages.newDueDate)}
                        value={newDueDate}
                        onChange={setNewDueDate}
                        error={dateError}
                        width="284px"
                        onOpen={() => setDisableBackdropClick(true)}
                        onClose={() => setDisableBackdropClick(false)}
                    />

                    <TextField
                        className={classes.addComment}
                        id="rdd-comments"
                        value={comments}
                        placeholder={intl.formatMessage(messages.requestComments)}
                        multiline
                        fullWidth
                        rows={4}
                        variant="outlined"
                        onChange={(e) => setComments(e.target.value)}
                        InputProps={{
                            inputProps: { maxLength: 300 },
                        }}
                    />
                    <div className={classes.characterCount}>{`${comments ? comments.length : 0}/300`}</div>
                </DialogContent>
                <DialogActions>
                    <Button
                        key="0"
                        disabled={!enabled}
                        onClick={(event) => {
                            event.target.disabled = true;
                            doDueDateRequest();
                        }}>
                        <FormattedMessage id="rdd-dialog-dialog.rdd-button" defaultMessage="Request" description="Request new due date button" />
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default RequestDueDateModal;
